import { FC, PropsWithChildren, createContext, useMemo } from 'react';
import { useTenantContext } from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import {
  AttributeFormat,
  AttributeProperty,
  AttributeStyle,
  GenericAttribute,
  ResolvedProperty
} from 'src/types/attributes';
import { asArray } from 'src/utils/tools';

export interface GenericAttributeEditorContext<
  T extends AttributeFormat = AttributeFormat
> {
  handlePropertyChanged: (
    property: ResolvedProperty<T>,
    value: AttributeProperty<T>['value']
  ) => void;
  handleAddAttribute: (attributeType?: string) => void;
  handleDeleteAttribute: (id: number) => void;
  // provide the resolved attributes
  attributes: GenericAttribute<T>[];
  // editor discrimination
  format: T;
}

export type AttributeEditorContext =
  | GenericAttributeEditorContext<'value'>
  | GenericAttributeEditorContext<'matrix'>;

export interface AttributeContextType {
  attributeType: string;
  attributeStyle: AttributeStyle;
  attributeValues: GenericAttribute[];
  editor?: AttributeEditorContext;
}

export const AttributeContext = createContext<AttributeContextType | undefined>(
  undefined
);

interface AttributeProviderProps {
  attributeType: string;
  // provide either multiple or a single attribute
  attributeValues?: GenericAttribute[] | GenericAttribute;
  // allow attribute editing
  editor?: AttributeEditorContext;
}

export const AttributeProvider: FC<
  PropsWithChildren<AttributeProviderProps>
> = (props) => {
  const { attributeType, attributeValues, editor, children } = props;

  const {
    style: { attributes }
  } = useTenantContext();

  const attributeStyle = useMemo(() => {
    const style = attributes[attributeType];
    if (style === undefined)
      // attribute style must be provided in tenant style for correct rendering
      console.warn(`[slots] No attribute style for ${attributeType}`);
    return style;
  }, [attributes, attributeType]);

  const attributesValuesOfType = useMemo(
    () =>
      asArray(attributeValues || []).filter(
        // extract the attributes for the current type in the current slot
        (attribute) => attribute.attributeType === attributeType
      ),
    [attributeValues, attributeType]
  );

  const context = useHookMemo({
    attributeType,
    attributeStyle,
    attributeValues: attributesValuesOfType,
    editor
  });

  return (
    <AttributeContext.Provider value={context}>
      {children}
    </AttributeContext.Provider>
  );
};
